import React, { useState, useEffect, useRef, useMemo } from "react";
import dayjs from "dayjs";
import { 
  Table, Input, Select, DatePicker, Drawer, Button, Modal, 
  Switch, Tooltip, Badge, Tabs, Tag, Row, Col, Space, Spin, Empty 
} from "antd";
import { 
  SearchOutlined, DownloadOutlined, SyncOutlined, 
  InfoCircleOutlined, ArrowUpOutlined,
  PlayCircleOutlined, PauseCircleOutlined, WarningOutlined
} from "@ant-design/icons";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import axiosClient from "../../config/axiosInstance.config";
import "./FalconLogMonitor.css";

const { RangePicker } = DatePicker;

// Native Date Formatting Helpers
const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  
  const pad = (num: number) => String(num).padStart(2, "0");
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const fromNow = (dateStr: string) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const formatTimeOnly = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const pad = (num: number) => String(num).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const OPENROUTER_MODELS = [
  'anthropic/claude-3.5-sonnet',
  'openai/gpt-4o',
  'deepseek/deepseek-chat',
  'google/gemini-flash-1.5',
  'meta-llama/llama-3.1-405b'
];

const WORKFLOW_TYPES = [
  'Annual Report Extraction',
  'Ticker Generation',
  'Manual Data Entry',
  'AI Recommend'
];

const FalconLogMonitor: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<any>(null);
  
  // Date and filter states
  const [dateFilter, setDateFilter] = useState<string>("30d"); // today, yesterday, 7d, 30d, 90d, custom
  const [reloadDate, setReloadDate] = useState<any>(null);
  const [customDateRange, setCustomDateRange] = useState<[any, any] | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  
  // Table search & pagination states
  const [leaderboardSearch, setLeaderboardSearch] = useState<string>("");
  const [leaderboardPlan, setLeaderboardPlan] = useState<string>("");
  
  // Drawer states
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Request Log Explorer states
  const [logSearch, setLogSearch] = useState<string>("");
  const [logUserFilter, setLogUserFilter] = useState<string>("");
  const [logModelFilter, setLogModelFilter] = useState<string>("");
  const [logStatusFilter, setLogStatusFilter] = useState<string>("");
  const [logWorkflowFilter, setLogWorkflowFilter] = useState<string>("");
  const [selectedLog, setSelectedLog] = useState<any>(null);
  
  // Live Stream Feed state (local cache to animate item additions)
  const [liveEvents, setLiveEvents] = useState<any[]>([]);
  
  // Auto refresh timer reference
  const refreshTimer = useRef<any>(null);

  const rangeLabel = useMemo(() => {
    switch (dateFilter) {
      case "today": return "Today";
      case "yesterday": return "Yesterday";
      case "7d": return "Last 7 Days";
      case "30d": return "Last 30 Days";
      case "90d": return "Last 90 Days";
      case "sinceLastReload":
        return data?.lastReloadTimestamp 
          ? `Since Reload: ${formatDate(data.lastReloadTimestamp)}` 
          : "Since Last Reload";
      case "custom": return "Custom Range";
      default: return "Selected Period";
    }
  }, [dateFilter, data?.lastReloadTimestamp]);

  const rangeSubLabel = useMemo(() => {
    switch (dateFilter) {
      case "today": return "Spend & activity today";
      case "yesterday": return "Spend & activity yesterday";
      case "7d": return "Activity over past 7 days";
      case "30d": return "Activity over past 30 days";
      case "90d": return "Activity over past 90 days";
      case "sinceLastReload": return "Activity since last report reload";
      case "custom": return "Activity in custom selected range";
      default: return "Activity in selected period";
    }
  }, [dateFilter]);

  // Fetch dashboard stats from backend
  const fetchStats = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      let start = "";
      let end = "";
      
      const now = new Date();
      if (dateFilter === "today") {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
        const e = new Date();
        e.setHours(23, 59, 59, 999);
        end = e.toISOString();
      } else if (dateFilter === "yesterday") {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
        const e = new Date();
        e.setDate(e.getDate() - 1);
        e.setHours(23, 59, 59, 999);
        end = e.toISOString();
      } else if (dateFilter === "7d") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
        end = now.toISOString();
      } else if (dateFilter === "30d") {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
        end = now.toISOString();
      } else if (dateFilter === "90d") {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        d.setHours(0, 0, 0, 0);
        start = d.toISOString();
        end = now.toISOString();
      } else if (dateFilter === "sinceLastReload") {
        start = "sinceLastReload";
        end = now.toISOString();
      } else if (dateFilter === "custom" && customDateRange) {
        start = new Date(customDateRange[0]).toISOString();
        end = new Date(customDateRange[1]).toISOString();
      }

      const params: any = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;

      const response = await axiosClient.get("/api/admin/log-monitor", { params });
      
      if (response.data && response.data.status === "success") {
        setData(response.data);
        
        if (response.data.lastReloadTimestamp) {
          const newDateStr = response.data.lastReloadTimestamp;
          setReloadDate((prev: any) => {
            if (!prev || prev.toISOString() !== newDateStr) {
              return dayjs(newDateStr);
            }
            return prev;
          });
        } else {
          setReloadDate(null);
        }

        // Merge & preserve live stream feed
        setLiveEvents(() => {
          const newEvents = response.data.liveStream || [];
          const combined = [...newEvents];
          // Filter duplicates by id
          const uniqueIds = new Set();
          return combined.filter(item => {
            if (uniqueIds.has(item.id)) return false;
            uniqueIds.add(item.id);
            return true;
          }).slice(0, 50);
        });
      }
    } catch (error) {
      console.error("Failed to fetch log monitor stats:", error);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  const handleReloadDateChange = async (date: any) => {
    if (!date) return;
    try {
      setLoading(true);
      const isoString = date.toISOString();
      const response = await axiosClient.post("/api/admin/log-monitor/reload-date", {
        reloadDate: isoString
      });
      if (response.data && response.data.status === "success") {
        setReloadDate(dayjs(response.data.lastReloadTimestamp));
        await fetchStats();
      }
    } catch (error) {
      console.error("Failed to update reload date:", error);
    } finally {
      setLoading(false);
    }
  };

  // Trigger data fetching when filters change
  useEffect(() => {
    fetchStats();
  }, [dateFilter, customDateRange]);

  // Set up polling interval
  useEffect(() => {
    if (autoRefresh) {
      refreshTimer.current = setInterval(() => {
        fetchStats(true);
      }, 5000);
    } else {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    }

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [autoRefresh, dateFilter, customDateRange]);

  // CSV/Excel Exporter for User Leaderboard
  const exportLeaderboard = (formatType: "csv" | "excel") => {
    if (!data?.leaderboard) return;
    
    const exportData = data.leaderboard.map((u: any) => ({
      "User Name": u.userName,
      "Email": u.email,
      "User ID": u.userId,
      "Subscription Plan": u.plan,
      "Total Cost (USD)": u.totalCost,
      "Total Tokens": u.totalTokens,
      "Workflows Executed": u.uniqueWorkflowCount || 0,
      "LLM Calls": u.workflowCount,
      "Ticker Workflows": u.tickerWorkflowCount,
      "Last Active": formatDate(u.lastActive),
      "Status": u.status
    }));

    const todayStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");

    if (formatType === "csv") {
      const headers = Object.keys(exportData[0]).join(",");
      const rows = exportData.map((row: any) => 
        Object.values(row).map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")
      );
      const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers, ...rows].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `falcon_users_leaderboard_${todayStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User Consumption");
      XLSX.writeFile(workbook, `falcon_users_leaderboard_${todayStr}.xlsx`);
    }
  };

  // CSV Exporter for Request Logs
  const exportLogs = () => {
    if (!data?.requestLogs) return;

    const logData = data.requestLogs.map((log: any) => ({
      "Timestamp": formatDate(log.timestamp),
      "User": log.userName,
      "Email": log.userEmail,
      "Workflow ID": log.workflowId,
      "Workflow Type": log.workflowType,
      "Request ID": log.id,
      "Model": log.model,
      "Prompt Tokens": log.promptTokens,
      "Completion Tokens": log.completionTokens,
      "Total Tokens": log.totalTokens,
      "Cost (USD)": log.costUsd,
      "Latency (ms)": log.latencyMs,
      "Status": log.status,
      "Error Message": log.errorMessage || ""
    }));

    const todayStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");

    const worksheet = XLSX.utils.json_to_sheet(logData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Request Logs");
    XLSX.writeFile(workbook, `falcon_request_logs_${todayStr}.xlsx`);
  };

  // Filtered Leaderboard computation
  const filteredLeaderboard = useMemo(() => {
    if (!data?.leaderboard) return [];
    return data.leaderboard.filter((u: any) => {
      const matchesSearch = 
        u.userName.toLowerCase().includes(leaderboardSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(leaderboardSearch.toLowerCase()) ||
        String(u.userId).includes(leaderboardSearch);
      
      const matchesPlan = leaderboardPlan ? u.plan === leaderboardPlan : true;
      
      return matchesSearch && matchesPlan;
    });
  }, [data?.leaderboard, leaderboardSearch, leaderboardPlan]);

  // Filtered Request Logs computation
  const filteredRequestLogs = useMemo(() => {
    if (!data?.requestLogs) return [];
    return data.requestLogs.filter((log: any) => {
      const matchesSearch = 
        log.userName.toLowerCase().includes(logSearch.toLowerCase()) ||
        log.userEmail.toLowerCase().includes(logSearch.toLowerCase()) ||
        log.workflowId.toLowerCase().includes(logSearch.toLowerCase()) ||
        log.id.toLowerCase().includes(logSearch.toLowerCase());

      const matchesUser = logUserFilter ? log.userEmail === logUserFilter : true;
      const matchesModel = logModelFilter ? log.model === logModelFilter : true;
      const matchesStatus = logStatusFilter ? log.status === logStatusFilter : true;
      const matchesWorkflow = logWorkflowFilter ? log.workflowType === logWorkflowFilter : true;

      return matchesSearch && matchesUser && matchesModel && matchesStatus && matchesWorkflow;
    });
  }, [data?.requestLogs, logSearch, logUserFilter, logModelFilter, logStatusFilter, logWorkflowFilter]);

  // Leaders table columns
  const leaderboardColumns = [
    {
      title: "User Profile",
      key: "user",
      render: (record: any) => (
        <div>
          <div className="font-semibold text-gray-900 dark:text-white">{record.userName}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{record.email}</div>
        </div>
      ),
      sorter: (a: any, b: any) => a.userName.localeCompare(b.userName)
    },
    {
      title: "User ID",
      dataIndex: "userId",
      key: "userId",
      className: "text-gray-600 dark:text-gray-300"
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan: string) => {
        let color = "blue";
        if (plan?.toLowerCase().includes("enterprise")) color = "purple";
        if (plan?.toLowerCase().includes("free")) color = "gray";
        return <Tag color={color} className="font-medium">{plan}</Tag>;
      }
    },
    {
      title: "Cost (USD)",
      dataIndex: "totalCost",
      key: "totalCost",
      render: (val: number) => <span className="font-semibold text-violet-600 dark:text-violet-400">${val.toFixed(4)}</span>,
      sorter: (a: any, b: any) => a.totalCost - b.totalCost
    },
    {
      title: "Tokens Consumed",
      dataIndex: "totalTokens",
      key: "totalTokens",
      render: (val: number) => val.toLocaleString(),
      sorter: (a: any, b: any) => a.totalTokens - b.totalTokens
    },

    {
      title: "Ticker Executions",
      dataIndex: "tickerExecutionCount",
      key: "tickerExecutionCount",
      render: (val: number) => <Tag color="purple">{val || 0}</Tag>,
      sorter: (a: any, b: any) => a.tickerExecutionCount - b.tickerExecutionCount
    },
    {
      title: "LLM Calls",
      dataIndex: "workflowCount",
      key: "workflowCount",
      render: (val: number) => <Tag color="cyan">{val}</Tag>,
      sorter: (a: any, b: any) => a.workflowCount - b.workflowCount
    },
    {
      title: "Last Active",
      dataIndex: "lastActive",
      key: "lastActive",
      render: (date: string) => fromNow(date),
      sorter: (a: any, b: any) => new Date(a.lastActive).getTime() - new Date(b.lastActive).getTime()
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Badge status={status === "Active" ? "success" : "default"} text={status} />
      )
    }
  ];

  // Request Log Explorer columns
  const requestLogColumns = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (ts: string) => formatDate(ts),
      sorter: (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    },
    {
      title: "User",
      key: "user",
      render: (record: any) => (
        <div>
          <div className="font-medium text-gray-800 dark:text-gray-200">{record.userName}</div>
          <div className="text-xs text-gray-500">{record.userEmail}</div>
        </div>
      )
    },
    {
      title: "Workflow",
      key: "workflow",
      render: (record: any) => (
        <div>
          <div className="text-xs text-gray-600 dark:text-gray-300 font-mono">{record.workflowId}</div>
          <Tag color="cyan" className="text-[10px] mt-0.5">{record.workflowType}</Tag>
        </div>
      )
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (model: string) => <span className="text-xs font-mono text-gray-700 dark:text-gray-300">{model.split("/")[1] || model}</span>
    },
    {
      title: "Cost (USD)",
      dataIndex: "costUsd",
      key: "costUsd",
      render: (val: number) => <span className="text-violet-500 font-semibold">${(val || 0).toFixed(5)}</span>,
      sorter: (a: any, b: any) => a.costUsd - b.costUsd
    },
    {
      title: "Tokens (P / C)",
      key: "tokens",
      render: (record: any) => (
        <div className="text-xs">
          <div><span className="font-semibold text-gray-700 dark:text-gray-300">{record.totalTokens.toLocaleString()}</span></div>
          <div className="text-[10px] text-gray-400 font-mono">{record.promptTokens} / {record.completionTokens}</div>
        </div>
      )
    },
    {
      title: "Latency",
      dataIndex: "latencyMs",
      key: "latencyMs",
      render: (val: number) => <span className="font-mono text-xs">{val.toLocaleString()} ms</span>,
      sorter: (a: any, b: any) => a.latencyMs - b.latencyMs
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: any) => {
        const color = status === "SUCCESS" ? "green" : "red";
        return (
          <Tooltip title={record.errorMessage}>
            <Tag color={color} className="font-semibold cursor-pointer">{status}</Tag>
          </Tooltip>
        );
      }
    }
  ];

  // Shared Chart configuration helper
  const getAreaChartOptions = (title: string, yLabel: string, color: string) => ({
    chart: {
      id: title.replace(/\s+/g, '-'),
      toolbar: { show: false },
      sparkline: { enabled: false },
      background: 'transparent'
    },
    colors: [color],
    stroke: { curve: 'smooth' as const, width: 2 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 95, 100]
      }
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.06)',
      strokeDashArray: 3
    },
    theme: { mode: 'dark' as const },
    xaxis: {
      categories: data?.creditConsumption?.dates || [],
      labels: {
        style: { colors: '#94A3B8' },
        formatter: (val: string) => {
          const d = new Date(val);
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: '#94A3B8' },
        formatter: (val: number) => val.toLocaleString()
      }
    },
    tooltip: { theme: 'dark' }
  });

  return (
    <div className="log-monitor-container p-6 min-h-screen bg-gray-50 text-gray-900 dark:bg-[#0f172a] dark:text-[#f8fafc] overflow-y-auto custom-scroll">
      
      {/* Header controls bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-[#334155]">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white m-0">Falcon Log Monitor</h1>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
              <span className={`pulse-indicator ${!autoRefresh ? 'paused' : ''}`}></span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-green-500">
                {autoRefresh ? "Live Sync Connected" : "Sync Standby"}
              </span>
            </div>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Core observability suite, LLM token metrics, and billing diagnostics.
          </p>
        </div>

        {/* Filters */}
        <Space wrap size="middle" className="w-full md:w-auto">
          <Select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e)}
            className="w-44 dark:bg-slate-800"
            popupClassName="log-monitor-select-dropdown"
          >
            <Select.Option value="today">Today</Select.Option>
            <Select.Option value="yesterday">Yesterday</Select.Option>
            <Select.Option value="7d">Last 7 Days</Select.Option>
            <Select.Option value="30d">Last 30 Days</Select.Option>
            <Select.Option value="90d">Last 90 Days</Select.Option>
            <Select.Option value="sinceLastReload">Since Last Reload</Select.Option>
            <Select.Option value="custom">Custom Range</Select.Option>
          </Select>

          {dateFilter === "sinceLastReload" && (
            <DatePicker 
              showTime
              value={reloadDate}
              onChange={handleReloadDateChange}
              placeholder="Select Reload Date & Time"
              className="dark:bg-slate-800 dark:text-white w-64"
              popupClassName="log-monitor-picker-dropdown"
            />
          )}

          {dateFilter === "custom" && (
            <RangePicker 
              value={customDateRange} 
              onChange={(dates: any) => setCustomDateRange(dates)}
              className="dark:bg-slate-800"
              popupClassName="log-monitor-picker-dropdown"
            />
          )}

          <div className="flex items-center gap-2 bg-gray-100 dark:bg-slate-800/80 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">Auto Refresh</span>
            <Switch 
              checked={autoRefresh} 
              onChange={setAutoRefresh} 
              size="small" 
              checkedChildren={<PlayCircleOutlined />}
              unCheckedChildren={<PauseCircleOutlined />}
            />
          </div>

          <Button 
            icon={<SyncOutlined />} 
            onClick={() => fetchStats()} 
            className="dark:bg-slate-800 dark:text-white"
          >
            Fetch
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {data ? (
          <Row gutter={[24, 24]}>
            
            {/* Dashboard main column */}
            <Col xs={24} lg={18} className="flex flex-col gap-6">
              
              {/* Executive Overview KPI grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="glass-card p-4 flex flex-col justify-between h-28">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Cost ({rangeLabel})</span>
                  <h2 className="text-2xl font-bold text-blue-500 m-0">${(data.executiveOverview.totalCostUsd || 0).toFixed(4)}</h2>
                  <span className="text-[10px] text-gray-500">{rangeSubLabel}</span>
                </div>
                <div className="glass-card p-4 flex flex-col justify-between h-28">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">LLM Cost Today</span>
                  <h2 className="text-2xl font-bold text-emerald-500 m-0">${(data.executiveOverview.costTodayUsd || 0).toFixed(4)}</h2>
                  <span className="text-[10px] text-emerald-500"><ArrowUpOutlined /> Realtime consumption today</span>
                </div>
                <div className="glass-card p-4 flex flex-col justify-between h-28">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Ticker Executions ({rangeLabel})</span>
                  <h2 className="text-2xl font-bold text-purple-500 m-0">{(data.executiveOverview.totalTickerExecutions || 0).toLocaleString()}</h2>
                  <span className="text-[10px] text-gray-500">Processed report statements</span>
                </div>
                <div className="glass-card p-4 flex flex-col justify-between h-28">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">API Requests</span>
                  <h2 className="text-2xl font-bold text-violet-500 m-0">{(data.executiveOverview.totalWorkflowsGenerated || 0).toLocaleString()}</h2>
                  <span className="text-[10px] text-gray-500">{rangeSubLabel}</span>
                </div>
                <div className="glass-card p-4 flex flex-col justify-between h-28">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Active Users ({rangeLabel})</span>
                  <h2 className="text-2xl font-bold text-amber-500 m-0">{data.executiveOverview.activeUsersInRange ?? data.executiveOverview.activeUsersToday} / {data.executiveOverview.totalUsers}</h2>
                  <span className="text-[10px] text-gray-500">{rangeSubLabel}</span>
                </div>
              </div>

              {/* Core Analytics Tabs */}
              <div className="glass-card p-6">
                <Tabs defaultActiveKey="1" className="log-monitor-tabs">
                  
                  {/* TAB 1: User Wise LLM Usage */}
                  <Tabs.TabPane tab="User Wise LLM Usage" key="1">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                      <Space wrap>
                        <Input 
                          placeholder="Search User, Email, ID..." 
                          prefix={<SearchOutlined />} 
                          value={leaderboardSearch}
                          onChange={(e) => setLeaderboardSearch(e.target.value)}
                          className="w-64 dark:bg-slate-800 dark:text-white"
                        />
                        <Select 
                          value={leaderboardPlan} 
                          onChange={setLeaderboardPlan} 
                          placeholder="Filter Plan"
                          allowClear
                          className="w-44 dark:bg-slate-800"
                          popupClassName="log-monitor-select-dropdown"
                        >
                          <Select.Option value="Free Plan">Free Plan</Select.Option>
                          <Select.Option value="Premium Plan">Premium Plan</Select.Option>
                          <Select.Option value="Enterprise Plan">Enterprise Plan</Select.Option>
                        </Select>
                      </Space>
                      
                      <Space>
                        <Button 
                          icon={<DownloadOutlined />} 
                          onClick={() => exportLeaderboard("csv")}
                          className="dark:bg-slate-800 dark:text-white"
                        >
                          CSV
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<DownloadOutlined />} 
                          onClick={() => exportLeaderboard("excel")}
                        >
                          Excel
                        </Button>
                      </Space>
                    </div>

                    <Table 
                      dataSource={filteredLeaderboard} 
                      columns={leaderboardColumns} 
                      rowKey="email"
                      pagination={{ pageSize: 8 }}
                      className="custom-table"
                      onRow={(record) => ({
                        onClick: () => {
                          setSelectedUser(record);
                          setDrawerVisible(true);
                        },
                        className: "cursor-pointer hover:bg-gray-100/50 dark:hover:bg-slate-800/40"
                      })}
                    />
                  </Tabs.TabPane>

                  {/* TAB 2: OpenRouter Models */}
                  <Tabs.TabPane tab="OpenRouter Models" key="2">
                    <Row gutter={[16, 16]} className="mb-4">
                      <Col xs={24} md={8}>
                        <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-lg flex items-center justify-between">
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">Most Used Model</div>
                            <div className="font-semibold text-sm mt-1 text-gray-900 dark:text-white font-mono">{data.modelInsights.mostUsedModel.split("/")[1] || data.modelInsights.mostUsedModel}</div>
                          </div>
                          <Badge status="processing" />
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div className="p-4 bg-purple-500/5 border border-purple-500/10 rounded-lg flex items-center justify-between">
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">Highest Cost Model</div>
                            <div className="font-semibold text-sm mt-1 text-gray-900 dark:text-white font-mono">{data.modelInsights.highestCostModel.split("/")[1] || data.modelInsights.highestCostModel}</div>
                          </div>
                          <span className="text-red-500 text-xs font-semibold"><ArrowUpOutlined /> Costly</span>
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-lg flex items-center justify-between">
                          <div>
                            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400">Highest Token Model</div>
                            <div className="font-semibold text-sm mt-1 text-gray-900 dark:text-white font-mono">{data.modelInsights.highestTokenModel.split("/")[1] || data.modelInsights.highestTokenModel}</div>
                          </div>
                          <span className="text-emerald-500 text-xs font-semibold"><InfoCircleOutlined /> Large Context</span>
                        </div>
                      </Col>
                    </Row>

                    <Table 
                      dataSource={data.openRouterUsage} 
                      columns={[
                        { title: "Model Name", dataIndex: "modelName", key: "modelName", render: (val: string) => <code className="text-blue-500 text-xs">{val}</code> },
                        { title: "Requests", dataIndex: "requestCount", key: "requestCount", sorter: (a: any, b: any) => a.requestCount - b.requestCount },
                        { title: "Prompt Tokens", dataIndex: "promptTokens", key: "promptTokens", render: (val: number) => val.toLocaleString() },
                        { title: "Completion Tokens", dataIndex: "completionTokens", key: "completionTokens", render: (val: number) => val.toLocaleString() },
                        { title: "Total Tokens", dataIndex: "totalTokens", key: "totalTokens", render: (val: number) => val.toLocaleString(), sorter: (a: any, b: any) => a.totalTokens - b.totalTokens },
                        { title: "Cost USD", dataIndex: "costUsd", key: "costUsd", render: (val: number) => <span className="text-violet-500 font-semibold">${val.toFixed(4)}</span> },
                        { title: "Avg Latency", dataIndex: "averageLatency", key: "averageLatency", render: (val: number) => `${val.toLocaleString()} ms` },
                        { title: "Success Rate", dataIndex: "successRate", key: "successRate", render: (val: number) => <Tag color={val > 95 ? "green" : "orange"}>{val}%</Tag> }
                      ]}
                      pagination={false}
                      className="custom-table"
                    />
                  </Tabs.TabPane>

                  {/* TAB 3: Usage Charts */}
                  <Tabs.TabPane tab="Usage Charts" key="3">
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={12}>
                        <div className="p-3 bg-gray-100/50 dark:bg-slate-900/40 rounded-xl">
                          <h3 className="text-sm font-semibold mb-4 text-gray-700 dark:text-gray-300">Cost USD Over Time</h3>
                          <Chart 
                            options={getAreaChartOptions("Costs", "Cost USD", "#8B5CF6")} 
                            series={[{ name: "Cost USD ($)", data: data.creditConsumption.costUsd }]} 
                            type="area" 
                            height={250} 
                          />
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className="p-3 bg-gray-100/50 dark:bg-slate-900/40 rounded-xl">
                          <h3 className="text-sm font-semibold mb-4 text-gray-700 dark:text-gray-300">Token Stacked Area Analysis</h3>
                          <Chart 
                            options={{
                              ...getAreaChartOptions("Tokens", "Tokens", "#10B981"),
                              chart: { ...getAreaChartOptions("Tokens", "Tokens", "#10B981").chart, stacked: true },
                              colors: ["#10B981", "#F59E0B", "#3B82F6"]
                            }} 
                            series={[
                              { name: "Prompt Tokens", data: data.creditConsumption.tokens.prompt },
                              { name: "Completion Tokens", data: data.creditConsumption.tokens.completion },
                              { name: "Total Tokens", data: data.creditConsumption.tokens.total }
                            ]} 
                            type="area" 
                            height={250} 
                          />
                        </div>
                      </Col>
                    </Row>
                  </Tabs.TabPane>

                  {/* TAB 4: Request Logs Explorer */}
                  <Tabs.TabPane tab="Request Logs Explorer" key="4">
                    <div className="flex flex-col gap-4 mb-4">
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={8}>
                          <Input 
                            placeholder="Search user, workflow, ID..." 
                            value={logSearch} 
                            onChange={(e) => setLogSearch(e.target.value)} 
                            prefix={<SearchOutlined />}
                            className="w-full dark:bg-slate-800 dark:text-white"
                          />
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Select 
                            value={logModelFilter} 
                            onChange={setLogModelFilter} 
                            placeholder="Filter Model" 
                            allowClear
                            className="w-full dark:bg-slate-800"
                            popupClassName="log-monitor-select-dropdown"
                          >
                            {OPENROUTER_MODELS.map(m => <Select.Option key={m} value={m}>{m.split("/")[1] || m}</Select.Option>)}
                          </Select>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                          <Select 
                            value={logStatusFilter} 
                            onChange={setLogStatusFilter} 
                            placeholder="Filter Status" 
                            allowClear
                            className="w-full dark:bg-slate-800"
                            popupClassName="log-monitor-select-dropdown"
                          >
                            <Select.Option value="SUCCESS">SUCCESS</Select.Option>
                            <Select.Option value="FAILED">FAILED</Select.Option>
                          </Select>
                        </Col>
                        <Col xs={24} sm={8} md={4} className="text-right">
                          <Button 
                            type="primary" 
                            icon={<DownloadOutlined />} 
                            onClick={exportLogs}
                            className="w-full"
                          >
                            Export Logs
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    <Table 
                      dataSource={filteredRequestLogs} 
                      columns={[
                        {
                          title: "Timestamp",
                          dataIndex: "timestamp",
                          key: "timestamp",
                          render: (ts: string) => formatDate(ts),
                          sorter: (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                        },
                        {
                          title: "User",
                          key: "user",
                          render: (record: any) => (
                            <div>
                              <div className="font-medium text-gray-800 dark:text-gray-200">{record.userName}</div>
                              <div className="text-xs text-gray-500">{record.userEmail}</div>
                            </div>
                          )
                        },
                        {
                          title: "Model",
                          dataIndex: "model",
                          key: "model",
                          render: (model: string) => <span className="text-xs font-mono text-gray-700 dark:text-gray-300">{model.split("/")[1] || model}</span>
                        },
                        {
                          title: "Cost (USD)",
                          dataIndex: "costUsd",
                          key: "costUsd",
                          render: (val: number) => <span className="text-violet-500 font-semibold">${(val || 0).toFixed(5)}</span>,
                          sorter: (a: any, b: any) => a.costUsd - b.costUsd
                        },
                        {
                          title: "Tokens (P / C)",
                          key: "tokens",
                          render: (record: any) => (
                            <div className="text-xs">
                              <div><span className="font-semibold text-gray-700 dark:text-gray-300">{record.totalTokens.toLocaleString()}</span></div>
                              <div className="text-[10px] text-gray-400 font-mono">{record.promptTokens} / {record.completionTokens}</div>
                            </div>
                          )
                        },
                        {
                          title: "Latency",
                          dataIndex: "latencyMs",
                          key: "latencyMs",
                          render: (val: number) => <span className="font-mono text-xs">{val.toLocaleString()} ms</span>,
                          sorter: (a: any, b: any) => a.latencyMs - b.latencyMs
                        },
                        {
                          title: "Status",
                          dataIndex: "status",
                          key: "status",
                          render: (status: string, record: any) => {
                            const color = status === "SUCCESS" ? "green" : "red";
                            return (
                              <Tooltip title={record.errorMessage}>
                                <Tag color={color} className="font-semibold cursor-pointer">{status}</Tag>
                              </Tooltip>
                            );
                          }
                        }
                      ]} 
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      className="custom-table"
                      onRow={(record) => ({
                        onClick: () => setSelectedLog(record),
                        className: "cursor-pointer hover:bg-gray-100/50 dark:hover:bg-slate-800/40"
                      })}
                    />
                  </Tabs.TabPane>

                  {/* TAB 5: Operational Health */}
                  <Tabs.TabPane tab="Operational Health" key="5">
                    <Row gutter={[24, 24]}>
                      
                      {/* Metric cards */}
                      <Col xs={24}>
                        <Row gutter={[16, 16]}>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">OpenRouter 429</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.errors429Count}</h2>
                            </div>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">OpenRouter 500</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.errors500Count}</h2>
                            </div>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">Timeouts</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.timeoutErrorsCount}</h2>
                            </div>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">Parsing Failures</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.parsingFailuresCount}</h2>
                            </div>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">Extraction Failures</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.extractionFailuresCount}</h2>
                            </div>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
                              <div className="text-[10px] text-gray-400 font-bold uppercase">Total Failures</div>
                              <h2 className="text-xl font-bold text-red-500 mt-1 m-0">{data.errorMonitoring.workflowFailuresCount}</h2>
                            </div>
                          </Col>
                        </Row>
                      </Col>

                      {/* Error Trends charts */}
                      <Col xs={24} md={12}>
                        <div className="p-3 bg-gray-100/50 dark:bg-slate-900/40 rounded-xl">
                          <h3 className="text-sm font-semibold mb-4 text-gray-700 dark:text-gray-300">Failure Count Trend</h3>
                          <Chart 
                            options={getAreaChartOptions("ErrorTrendCount", "Failures", "#EF4444")} 
                            series={[{ name: "Errors", data: data.errorMonitoring.trend.errorCount }]} 
                            type="line" 
                            height={220} 
                          />
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className="p-3 bg-gray-100/50 dark:bg-slate-900/40 rounded-xl">
                          <h3 className="text-sm font-semibold mb-4 text-gray-700 dark:text-gray-300">Daily Error Rate (%)</h3>
                          <Chart 
                            options={getAreaChartOptions("ErrorRateChart", "Error Rate (%)", "#EF4444")} 
                            series={[{ name: "Error Rate (%)", data: data.errorMonitoring.trend.errorRate }]} 
                            type="line" 
                            height={220} 
                          />
                        </div>
                      </Col>

                      {/* Recent Failures Table */}
                      <Col xs={24}>
                        <div className="p-4 bg-gray-100/50 dark:bg-slate-900/40 rounded-xl">
                          <h3 className="text-sm font-semibold mb-4 text-gray-700 dark:text-gray-300">Recent Operational Incidents</h3>
                          <Table 
                            dataSource={data.errorMonitoring.recentFailures}
                            rowKey={(record, idx) => `err-${idx}`}
                            columns={[
                              { title: "Timestamp", dataIndex: "timestamp", key: "timestamp", render: (ts: string) => formatDate(ts) },
                              { title: "User Email", dataIndex: "userEmail", key: "userEmail" },
                              { title: "Workflow ID", dataIndex: "workflowId", key: "workflowId", render: (val: string) => <code className="text-xs">{val}</code> },
                              { title: "Workflow Type", dataIndex: "workflowType", key: "workflowType" },
                              { title: "Error Message", dataIndex: "errorMessage", key: "errorMessage", render: (msg: string) => <span className="text-red-500 font-medium">{msg}</span> },
                              { title: "Error Type", dataIndex: "errorType", key: "errorType", render: (t: string) => <Tag color="red">{t}</Tag> }
                            ]}
                            pagination={{ pageSize: 5 }}
                            className="custom-table"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Tabs.TabPane>

                </Tabs>
              </div>
            </Col>

            {/* Live stream side column (4 columns) */}
            <Col xs={24} lg={6}>
              <div className="glass-card p-4 h-full flex flex-col max-h-[920px]">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200 dark:border-[#334155] mb-4">
                  <div>
                    <h3 className="text-sm font-bold tracking-tight text-gray-900 dark:text-white m-0">Live Observability Feed</h3>
                    <div className="text-[10px] text-gray-400 mt-0.5">Updated in real-time (5s)</div>
                  </div>
                  <Badge status="processing" color="#10B981" />
                </div>

                {/* Event list scrolling */}
                <div className="flex-1 overflow-y-auto pr-1 custom-scroll flex flex-col gap-3">
                  {liveEvents.length > 0 ? (
                    liveEvents.map((event) => {
                      let tagColor = "blue";
                      if (event.actionType?.includes("Failed")) tagColor = "red";
                      if (event.actionType?.includes("Completed")) tagColor = "green";
                      if (event.actionType?.includes("Credit")) tagColor = "yellow";
                      if (event.actionType?.includes("Login")) tagColor = "purple";
                      if (event.actionType?.includes("Admin")) tagColor = "orange";
                      
                      return (
                        <div 
                          key={event.id + event.timestamp} 
                          className="stream-item p-3 bg-white dark:bg-slate-900/60 rounded-lg border border-gray-200 dark:border-slate-800/80 hover:border-blue-500/50 transition-all cursor-pointer"
                          onClick={() => setSelectedLog(event)}
                        >
                          <div className="flex justify-between items-start">
                            <Tag color={tagColor} className="text-[9px] font-bold py-0.5 px-1.5">{event.actionType}</Tag>
                            <span className="text-[9px] text-gray-400 font-mono">{formatTimeOnly(event.timestamp)}</span>
                          </div>
                          
                          <div className="text-xs font-semibold text-gray-800 dark:text-gray-200 mt-2">
                            {event.userName}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {event.userEmail}
                          </div>

                          <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100 dark:border-slate-800 text-[10px]">
                            <span className="text-gray-400 truncate w-24 font-mono">{event.model.split("/")[1] || event.model}</span>
                            <span className="text-purple-500 font-bold">${(event.costUsd || 0).toFixed(5)}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <Empty description="No live events detected" />
                  )}
                </div>
              </div>
            </Col>

          </Row>
        ) : (
          <div className="flex justify-center items-center h-96">
            <Spin size="large" />
          </div>
        )}
      </Spin>

      {/* User Analytics Details Drawer */}
      <Drawer
        title={<div className="font-bold text-lg text-gray-900 dark:text-white">User Analytics Details</div>}
        placement="right"
        width={720}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        className="log-monitor-drawer dark:bg-slate-900"
      >
        {selectedUser && (
          <div className="flex flex-col gap-6 text-gray-800 dark:text-slate-200">
            
            {/* User Profile */}
            <div className="p-4 bg-gray-100/50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700/60">
              <h3 className="text-xs uppercase font-bold tracking-wider text-gray-400 mb-3">User Profile</h3>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div className="text-xs text-gray-500">Full Name</div>
                  <div className="font-semibold text-sm">{selectedUser.userName}</div>
                </Col>
                <Col span={12}>
                  <div className="text-xs text-gray-500">Email Address</div>
                  <div className="font-semibold text-sm">{selectedUser.email}</div>
                </Col>
                <Col span={12}>
                  <div className="text-xs text-gray-500">User ID</div>
                  <div className="font-mono text-sm">{selectedUser.userId}</div>
                </Col>
                <Col span={12}>
                  <div className="text-xs text-gray-500">Plan Status</div>
                  <Tag color="purple" className="mt-0.5">{selectedUser.plan}</Tag>
                </Col>
              </Row>
            </div>

            {/* Consumption Metrics */}
            <div className="p-4 bg-gray-100/50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700/60">
              <h3 className="text-xs uppercase font-bold tracking-wider text-gray-400 mb-3">Consumption Analytics</h3>
              <Row gutter={[16, 16]}>

                <Col span={8}>
                  <div className="text-xs text-gray-500">Ticker Executions</div>
                  <div className="text-lg font-bold text-purple-500">{selectedUser.tickerExecutionCount || 0}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Total API Calls</div>
                  <div className="text-lg font-bold text-blue-500">{selectedUser.workflowCount}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Total Cost (USD)</div>
                  <div className="text-lg font-bold text-purple-500">${(selectedUser.totalCost || 0).toFixed(4)}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Total Tokens</div>
                  <div className="text-lg font-bold text-rose-500">{(selectedUser.totalTokens || 0).toLocaleString()}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Prompt Tokens</div>
                  <div className="font-mono font-semibold">{(selectedUser.promptTokens || 0).toLocaleString()}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Completion Tokens</div>
                  <div className="font-mono font-semibold">{(selectedUser.completionTokens || 0).toLocaleString()}</div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Avg Tokens / Call</div>
                  <div className="font-mono font-semibold">
                    {selectedUser.workflowCount > 0 ? Math.round(selectedUser.totalTokens / selectedUser.workflowCount).toLocaleString() : 0}
                  </div>
                </Col>
                <Col span={8}>
                  <div className="text-xs text-gray-500">Avg Calls / Workflow</div>
                  <div className="font-mono font-semibold text-amber-500">
                    {selectedUser.uniqueWorkflowCount > 0 ? (selectedUser.workflowCount / selectedUser.uniqueWorkflowCount).toFixed(1) : 0}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Usage charts */}
            <div className="p-4 bg-gray-100/50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700/60">
              <h3 className="text-xs uppercase font-bold tracking-wider text-gray-400 mb-3">Usage Trends</h3>
              <Tabs defaultActiveKey="1" size="small">
                <Tabs.TabPane tab="Cost USD" key="1">
                  <Chart 
                    options={{
                      ...getAreaChartOptions("UserCostChart", "Cost USD", "#8B5CF6"),
                      xaxis: { categories: (selectedUser.dailyCostUsd || []).map((c: any) => c.date) }
                    }} 
                    series={[{ name: "Cost USD ($)", data: (selectedUser.dailyCostUsd || []).map((c: any) => c.value) }]} 
                    type="area" 
                    height={200} 
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Tokens" key="2">
                  <Chart 
                    options={{
                      ...getAreaChartOptions("UserTokensChart", "Tokens", "#10B981"),
                      xaxis: { categories: (selectedUser.dailyTokens || []).map((t: any) => t.date) }
                    }} 
                    series={[{ name: "Tokens Consumed", data: (selectedUser.dailyTokens || []).map((t: any) => t.value) }]} 
                    type="area" 
                    height={200} 
                  />
                </Tabs.TabPane>
              </Tabs>
            </div>

            {/* Recent activity audit */}
            <div className="p-4 bg-gray-100/50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700/60">
              <h3 className="text-xs uppercase font-bold tracking-wider text-gray-400 mb-3">User session Audit (Last 100 Events)</h3>
              <Table 
                dataSource={selectedUser.events}
                rowKey={(record, idx) => `user-evt-${idx}`}
                columns={[
                  { title: "Timestamp", dataIndex: "timestamp", key: "timestamp", render: (ts: string) => formatDate(ts) },
                  { title: "Action", dataIndex: "actionType", key: "actionType", render: (val: string) => <Tag color="blue">{val}</Tag> },
                  { title: "Workflow", dataIndex: "workflowType", key: "workflowType" },
                  { title: "Cost (USD)", dataIndex: "costUsd", key: "costUsd", render: (val: number) => <span>${(val || 0).toFixed(5)}</span> }
                ]}
                pagination={{ pageSize: 5 }}
                className="custom-table"
              />
            </div>

          </div>
        )}
      </Drawer>

      {/* Drill Down details Modal for individual requests */}
      <Modal
        title={<div className="font-bold text-gray-900 dark:text-white">API Call Diagnostics</div>}
        visible={!!selectedLog}
        onCancel={() => setSelectedLog(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSelectedLog(null)}>Close</Button>
        ]}
        className="log-monitor-modal dark:bg-slate-900"
      >
        {selectedLog && (
          <div className="flex flex-col gap-4 font-sans text-gray-800 dark:text-slate-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-400">TIMESTAMP</div>
                <div className="text-sm font-semibold">{formatDate(selectedLog.timestamp)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">REQUEST ID</div>
                <div className="text-sm font-semibold font-mono">{selectedLog.id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">USER</div>
                <div className="text-sm font-semibold">{selectedLog.userName} ({selectedLog.userEmail})</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">WORKFLOW ID</div>
                <div className="text-sm font-semibold font-mono">{selectedLog.workflowId}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">LLM MODEL</div>
                <div className="text-sm font-semibold font-mono text-blue-500">{selectedLog.model}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">STATUS</div>
                <Tag color={selectedLog.status === "SUCCESS" ? "green" : "red"} className="font-bold mt-1">
                  {selectedLog.status}
                </Tag>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-slate-800 pt-3 mt-2">
              <h4 className="text-xs uppercase font-bold tracking-wider text-gray-400 mb-2">Metrics Summary</h4>
              <div className="grid grid-cols-3 gap-2 text-center bg-gray-100 dark:bg-slate-900/50 p-3 rounded-lg border border-gray-200 dark:border-slate-800">
                <div>
                  <div className="text-[10px] text-gray-400">PROMPT TOKENS</div>
                  <div className="text-sm font-bold">{selectedLog.promptTokens.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400">COMPLETION TOKENS</div>
                  <div className="text-sm font-bold">{selectedLog.completionTokens.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400">TOKENS CONSUMED</div>
                  <div className="text-sm font-bold text-blue-500">{selectedLog.totalTokens.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <div className="text-xs text-gray-400 font-bold uppercase">Latency</div>
                <div className="font-semibold text-gray-900 dark:text-white text-lg">{selectedLog.latencyMs.toLocaleString()} ms</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-bold uppercase">Cost (USD)</div>
                <div className="font-semibold text-violet-500 text-lg">${selectedLog.costUsd.toFixed(5)}</div>
              </div>
            </div>

            {selectedLog.status === "FAILED" && (
              <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-lg mt-2 text-red-500">
                <div className="text-xs font-bold uppercase"><WarningOutlined /> DIAGNOSTIC FAULT DETAILS</div>
                <div className="text-sm mt-1 font-semibold">{selectedLog.errorMessage}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
};

export default FalconLogMonitor;
